import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
} from 'react'
import { useAtom } from 'jotai'
import { createUseStyles } from 'react-jss'
import { CaseDetail } from '../../../../models/CaseDetail'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { Overlay } from '../../../ui/Sidebar'
import { RemindersHeaderInfo } from '../Reminders/Reminders'
import { cardProps, control } from '../../../../theme/reuseables'
import { CaseNoteCard } from './CaseNoteCard'
import CaseNoteCreateView from './CaseNoteCreateView'
import { L } from '../../../providers/Lokalise'
import { profileAtom } from '../../../../api/profile/profile'

type CaseNotesProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
}

export type CaseNotesOverlayRef = {
    openOverlay: (noteId?: string) => void
}

const useStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: '100%',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

const CaseNotesOverlay = forwardRef<CaseNotesOverlayRef, CaseNotesProps>(
    ({ caseDetails, onUpdated }, ref): JSX.Element => {
        const [isOpen, setIsOpen] = useState(false)
        const [noteToScrollTo, setNoteToScrollTo] = useState<
            string | undefined
        >()
        const classes = useStyles()
        const [view, setView] = useState<'list' | 'create'>('list')
        const [authProfile] = useAtom(profileAtom)

        useImperativeHandle(ref, () => ({
            openOverlay: (noteId?: string) => {
                setNoteToScrollTo(noteId)
                setIsOpen(true)
            },
        }))

        useEffect(() => {
            if (isOpen && noteToScrollTo) {
                const element = document.getElementById(
                    `note-${noteToScrollTo}`
                )
                if (element) {
                    // Find the scrolling container
                    const scrollContainer = element.closest('.visible')

                    if (scrollContainer) {
                        // Get element position relative to container
                        const elementRect = element.getBoundingClientRect()
                        const containerRect =
                            scrollContainer.getBoundingClientRect()
                        const elementTop = element.offsetTop

                        // Scroll to position with header clearance (120px)
                        scrollContainer.scrollTo({
                            top: elementTop - 120,
                            behavior: 'smooth',
                        })
                    } else {
                        // Fallback to normal scroll
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }

                    // Apply effect to the CaseNoteCard (first child)
                    const noteCard = element.firstElementChild as HTMLElement
                    if (noteCard) {
                        // Add smooth transition
                        noteCard.style.transition = 'all 0.3s ease-in-out'

                        // Apply highlight effect
                        noteCard.style.backgroundColor = colors.green200
                        noteCard.style.boxShadow = `0 0 0 3px ${colors.green}40`
                        noteCard.style.borderRadius = '8px'
                        noteCard.style.transform = 'scale(1.02)'

                        // Remove effect after 2 seconds
                        setTimeout(() => {
                            noteCard.style.backgroundColor = 'transparent'
                            noteCard.style.boxShadow = 'none'
                            noteCard.style.transform = 'scale(1)'

                            // Clean up transition after effect is done
                            setTimeout(() => {
                                noteCard.style.transition = ''
                            }, 300)
                        }, 2000)
                    }
                }
            }
        }, [isOpen, noteToScrollTo])

        const caseNotesListView = (): JSX.Element => {
            return (
                <>
                    <div
                        className={`${classes.contentWrapper} ${
                            isOpen ? 'visible' : 'visible'
                        }`}
                    >
                        <Typography
                            tag="h2"
                            size="XL"
                            align="center"
                            weight="bold"
                            style={{ margin: '20px 0 10px' }}
                        >
                            {L('LØN_SAGS_NOTE_SIDEBAR_TITEL')}
                        </Typography>
                        {!caseDetails?.isClosed && (
                            <div
                                style={{
                                    marginBottom: '2rem',
                                    marginTop: '2rem',
                                    display: 'flex',
                                    justifyContent: 'end',
                                }}
                            >
                                <Button
                                    onClick={() => setView('create')}
                                    size="medium"
                                    disabled={
                                        !caseDetails?.allowedUpdateActionList.includes(
                                            'Sagsbehandle'
                                        ) || authProfile.isImitator
                                    }
                                >
                                    {L(
                                        'LØN_SAGS_NOTE_SIDEBAR_TILFØJ_NOTAT_KNAP'
                                    )}
                                </Button>
                            </div>
                        )}
                        <div style={{ paddingBottom: '100dvh' }}>
                            {caseDetails?.noteList.map((note) => (
                                <div id={`note-${note.id}`} key={note.id}>
                                    <CaseNoteCard
                                        note={note}
                                        currentCase={caseDetails}
                                        onUpdated={() => {
                                            onUpdated?.()
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )
        }

        return (
            <>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        type="action"
                        onClick={() => setIsOpen(true)}
                        size="xlarge"
                        data-testid="show-all-notes-button"
                        style={{
                            fontSize: '.9rem',
                            minHeight: '1px',
                            textDecoration: 'underline',
                        }}
                    >
                        {L('LØN_SAGS_NOTE_SIDEBAR_VIS_ALLE_KNAP')}
                    </Button>
                </div>

                <Overlay
                    isShowing={isOpen}
                    hide={() => {
                        setView('list')
                        setIsOpen(false)
                    }}
                >
                    <RemindersHeaderInfo
                        name={caseDetails?.newlyHired.name}
                        caseNumber={caseDetails?.caseNumber}
                    />
                    {view === 'list' && caseNotesListView()}
                    {view === 'create' && (
                        <CaseNoteCreateView
                            caseDetails={caseDetails}
                            onUpdated={() => {
                                setView('list')
                                onUpdated?.()
                            }}
                            onCancel={() => setView('list')}
                            isOpen={isOpen}
                        />
                    )}
                </Overlay>
            </>
        )
    }
)

export default CaseNotesOverlay
